import { forwardRef, useEffect, type HTMLAttributes } from 'react';
import { cn } from '../../utils/cn';
import type { Project } from './Portfolio';

export interface ProjectModalProps extends HTMLAttributes<HTMLDivElement> {
  project: Project;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * ProjectModal Component
 * 
 * Modal per mostrare dettagli progetto.
 */
export const ProjectModal = forwardRef<HTMLDivElement, ProjectModalProps>(
  ({ project, isOpen, onClose, className, ...props }, ref) => {
    // Chiudi con ESC
    useEffect(() => {
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && isOpen) {
          onClose();
        }
      };

      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    // Previeni scroll
    useEffect(() => {
      if (isOpen) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }

      return () => {
        document.body.style.overflow = '';
      };
    }, [isOpen]);

    if (!isOpen) return null;

    return (
      <>
        {/* Overlay */}
        <div
          className="fixed inset-0 bg-black/70 z-50 transition-opacity"
          onClick={onClose}
          aria-hidden="true"
        />

        {/* Modal */}
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div
              ref={ref}
              className={cn(
                'relative bg-base-100 rounded-2xl shadow-2xl max-w-4xl w-full',
                'transform transition-all',
                className
              )}
              {...props}
            >
              {/* Close Button */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-base-100 hover:bg-base-200 shadow-lg flex items-center justify-center transition-colors"
                aria-label="Chiudi"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {/* Image */}
              <div className="relative aspect-video w-full overflow-hidden rounded-t-2xl">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Content */}
              <div className="p-8">
                {project.category && (
                  <span className="inline-block px-3 py-1 bg-primary text-primary-content text-sm font-semibold rounded-full mb-4">
                    {project.category}
                  </span>
                )}

                <h2 className="text-3xl font-bold text-base-content mb-4">
                  {project.title}
                </h2>

                <p className="text-lg text-base-content/80 mb-6">
                  {project.description}
                </p>

                {project.details && (
                  <div className="prose prose-lg max-w-none mb-6">
                    <p className="text-base-content/70">{project.details}</p>
                  </div>
                )}

                {/* Tags */}
                {project.tags && project.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-6">
                    {project.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-4 py-2 bg-base-200 text-base-content rounded-lg text-sm font-medium"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                {/* Link */}
                {project.link && (
                  <a
                    href={project.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-content rounded-lg hover:bg-primary-focus transition-colors font-medium"
                  >
                    Visita il progetto
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }
);

ProjectModal.displayName = 'ProjectModal';

